import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "./AuthContext";

const CODE_LENGTH = 6;

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalBoxStyle = {
  background: "#fff",
  borderRadius: 14,
  padding: "28px 26px",
  width: "90vw",
  maxWidth: 380,
  boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
  textAlign: "center",
};

const closeButtonStyle = {
  position: "absolute",
  top: 14,
  right: 16,
  background: "none",
  border: "none",
  fontSize: 20,
  cursor: "pointer",
  lineHeight: 1,
  color: "#999",
};

const iconBadgeStyle = {
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: "rgba(0,191,255,0.12)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto",
};

const titleStyle = {
  fontSize: 18,
  fontWeight: 700,
  marginBottom: 6,
  color: "#111",
};

const descriptionStyle = {
  fontSize: 13,
  color: "#666",
  marginBottom: 22,
  lineHeight: 1.5,
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: "#555",
  display: "block",
  textAlign: "left",
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #ccc",
  boxSizing: "border-box",
  marginBottom: 16,
  fontSize: 14,
};

const otpRowStyle = {
  display: "flex",
  justifyContent: "center",
  gap: 8,
  marginBottom: 18,
};

const otpBoxStyle = {
  width: 42,
  height: 50,
  textAlign: "center",
  fontSize: 20,
  fontWeight: 700,
  borderRadius: 8,
  border: "1px solid #ccc",
  boxSizing: "border-box",
};

const primaryButton = {
  width: "100%",
  padding: "13px",
  borderRadius: "8px",
  background: "#00bfff",
  border: "none",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: 0.2,
  cursor: "pointer",
};

const secondaryButton = {
  width: "100%",
  padding: "13px",
  borderRadius: "8px",
  background: "#ffc400",
  border: "none",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: 0.2,
  cursor: "pointer",
  marginTop: 10,
};

const linkButton = {
  display: "block",
  width: "100%",
  background: "none",
  border: "none",
  color: "#00bfff",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 13,
  marginTop: 14,
  padding: 0,
  textAlign: "center",
};

const alertStyle = (isError) => ({
  fontSize: 13,
  marginBottom: 16,
  padding: "8px 10px",
  borderRadius: 6,
  background: isError ? "rgba(220,20,60,0.08)" : "rgba(0,135,79,0.08)",
  color: isError ? "crimson" : "#00874f",
});

function LockIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00bfff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00874f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function Header({ icon, title, description }) {
  return (
    <>
      <div style={iconBadgeStyle}>{icon}</div>
      <div style={titleStyle}>{title}</div>
      <div style={descriptionStyle}>{description}</div>
    </>
  );
}

export default function WorkOSVerifyModal({ initialUsername, onClose }) {
  const navigate = useNavigate();
  const { login } = useAuth();

  // username -> code -> verified -> changePassword -> done
  const [step, setStep] = useState("username");
  // "mfa" (verify + continue to account) or "reset" (verify + set new password, no auto-login)
  const [intent, setIntent] = useState("mfa");
  const [username, setUsername] = useState(initialUsername || "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [authData, setAuthData] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const codeRefs = useRef([]);

  const handleCodeDigitChange = (index, rawValue) => {
    const digit = rawValue.replace(/[^0-9]/g, "").slice(-1);
    const chars = code.split("");
    chars[index] = digit;
    const next = chars.join("").slice(0, CODE_LENGTH);
    setCode(next);
    if (digit && index < CODE_LENGTH - 1) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, CODE_LENGTH);
    if (!pasted) return;
    setCode(pasted);
    codeRefs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleSendCode = async (e, intentValue = "mfa") => {
    if (e?.preventDefault) e.preventDefault();
    setError("");

    if (!username.trim()) {
      setError("Please enter your username.");
      return;
    }

    setIntent(intentValue);
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/workos/send-code`, { username });
      setMessage("A 6-digit verification code has been sent to the email on file for this account.");
      setStep("code");
    } catch (err) {
      setError(err.response?.data?.error || "Could not send verification code.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");

    if (code.length !== CODE_LENGTH) {
      setError("Enter the full 6-digit code.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/workos/verify-code`, {
        username,
        code,
      });

      setAuthData(response.data);
      setMessage("");
      // Lost-password entry point skips the "verified / Continue" screen
      // and goes straight to setting a new password.
      setStep(intent === "reset" ? "changePassword" : "verified");
    } catch (err) {
      setError(err.response?.data?.error || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/workos/change-password`, {
        resetToken: authData.resetToken,
        newPassword,
      });
      setMessage("Password updated successfully.");
      setStep("done");
    } catch (err) {
      setError(err.response?.data?.error || "Could not update password.");
    } finally {
      setLoading(false);
    }
  };

  // Mirrors SignIn.jsx's post-login redirect exactly, so a completed
  // MFA verification lands the user in the same place a normal login would.
  const handleContinue = () => {
    login({ username: authData.username, account_id: authData.alpacaAccountId });
    localStorage.setItem("spectraToken", authData.token);
    onClose();

    if (authData.alpacaStatus === "NONE") {
      navigate("/Dashboard");
    } else {
      window.location.href = `${process.env.REACT_APP_PORTFOLIO_URL}/?handoff=${authData.handoffCode}`;
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalBoxStyle, position: "relative" }} onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} style={closeButtonStyle}>
          ✕
        </button>

        {step === "username" && (
          <Header
            icon={<LockIcon />}
            title="Security Verification"
            description="Enter your username and we'll email a one-time verification code to the address on file for this account."
          />
        )}

        {step === "code" && (
          <Header
            icon={<LockIcon />}
            title="Enter Verification Code"
            description={`A 6-digit code was sent to the email on file for "${username}". Enter it below to continue.`}
          />
        )}

        {step === "verified" && (
          <Header
            icon={<CheckIcon />}
            title="Identity Verified"
            description="Your identity has been confirmed. Continue to your account, or update your password below."
          />
        )}

        {step === "changePassword" && (
          <Header
            icon={<LockIcon />}
            title="Create New Password"
            description="Choose a new password for your Spectra account."
          />
        )}

        {step === "done" && (
          <Header
            icon={<CheckIcon />}
            title="Password Updated"
            description={
              intent === "reset"
                ? "Your password has been changed successfully. You can close this window and sign in with your new password."
                : "Your password has been changed successfully."
            }
          />
        )}

        {error && <div style={alertStyle(true)}>{error}</div>}
        {!error && message && <div style={alertStyle(false)}>{message}</div>}

        {step === "username" && (
          <form onSubmit={handleSendCode}>
            <label style={labelStyle}>Username</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={inputStyle}
              placeholder="Username"
              autoFocus
            />
            <button type="submit" style={primaryButton} disabled={loading}>
              {loading ? "Sending..." : "Send Verification Code"}
            </button>
            <button
              type="button"
              style={secondaryButton}
              disabled={loading}
              onClick={(e) => handleSendCode(e, "reset")}
            >
              {loading ? "Sending..." : "Lost Password"}
            </button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={handleVerifyCode}>
            <div style={otpRowStyle} onPaste={handleCodePaste}>
              {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                <input
                  key={i}
                  ref={(el) => (codeRefs.current[i] = el)}
                  value={code[i] || ""}
                  onChange={(e) => handleCodeDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  maxLength={1}
                  inputMode="numeric"
                  autoFocus={i === 0}
                  style={otpBoxStyle}
                />
              ))}
            </div>
            <button type="submit" style={primaryButton} disabled={loading}>
              {loading ? "Verifying..." : "Verify Code"}
            </button>
            <button type="button" style={linkButton} onClick={() => setStep("username")}>
              Use a different username
            </button>
          </form>
        )}

        {step === "verified" && (
          <div>
            <button type="button" style={primaryButton} onClick={handleContinue}>
              Continue
            </button>
            <button type="button" style={linkButton} onClick={() => setStep("changePassword")}>
              Change Password Instead
            </button>
          </div>
        )}

        {step === "changePassword" && (
          <form onSubmit={handleChangePassword}>
            <label style={labelStyle}>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={inputStyle}
              placeholder="New password"
              autoFocus
            />
            <label style={labelStyle}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={inputStyle}
              placeholder="Confirm new password"
            />
            <button type="submit" style={primaryButton} disabled={loading}>
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}

        {step === "done" && (
          <div>
            {intent === "reset" ? (
              <button type="button" style={primaryButton} onClick={onClose}>
                Close
              </button>
            ) : (
              <button type="button" style={primaryButton} onClick={handleContinue}>
                Continue
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
